import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Tooltip, type Selection } from '@heroui/react';
import { DataGrid, type DataGridColumn } from '@heroui-pro/react';
import type { ChartAccount, LocaleCode } from '@/api/models';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { formatGel, type AccountTreeNode } from '../../utils';
import {
    COA_COLUMN,
    COA_CONTENT_CLASS,
    COA_HEADING_HEIGHT_PX,
    COA_INDENT_STEP_PX,
    COA_ROW_HEIGHT_PX,
    COA_TREE_COLUMN,
    getAccountNameClassName,
    getBalanceClassName,
    isNavigableAccountRow,
} from '../chartOfAccountsMeta';
import { AccountTypeChip } from '../accountTypeChip';
import { useChevronAriaLabels } from './useChevronAriaLabels';

type AccountTreeProps = {
    roots: AccountTreeNode[];
    expandedKeys: Set<string>;
    onExpandedChange: (keys: Selection) => void;
    locale: LocaleCode;
    onSelectAccount: (account: ChartAccount) => void;
    /** Open the create/edit drawer for a row. */
    onEditAccount: (account: ChartAccount) => void;
    /** Un-archive an archived row (always allowed — no balance precondition). */
    onReactivateAccount: (account: ChartAccount) => void;
    /**
     * Whether this session may write. A read-only chart renders no row actions
     * at all — the rows still navigate to their entries, which is a read.
     *
     * There is no delete action here in either mode. `DELETE /accounts/{id}`
     * exists on the API; accounts are archived, never deleted, so it is
     * unreachable from the UI by design.
     */
    canWrite: boolean;
};

/** Flatten the visible tree into an id→node map for row-action lookup. */
const indexNodes = (
    nodes: AccountTreeNode[],
    into: Map<string, AccountTreeNode> = new Map(),
): Map<string, AccountTreeNode> => {
    for (const node of nodes) {
        into.set(node.account.id, node);
        indexNodes(node.children, into);
    }
    return into;
};

/**
 * Render-only account tree, built on HeroUI Pro's `DataGrid` with native
 * expandable rows. The nested `AccountTreeNode[]` is fed straight in; DataGrid
 * renders the chevron toggle in the ACCOUNT (`treeColumn`) and indents each
 * level by `treeIndent`. Row banding, hover reveal of the "View entries"
 * affordance, and the leaf cursor are handled by the scoped `.coa-data-grid`
 * rules in `index.css` (DataGrid exposes no per-row className, so those key off
 * its row/cell state attributes).
 */
export const AccountTree: React.FC<AccountTreeProps> = ({
    roots,
    expandedKeys,
    onExpandedChange,
    locale,
    onSelectAccount,
    onEditAccount,
    onReactivateAccount,
    canWrite,
}) => {
    const { t } = useTranslation('pages');

    const nodesById = React.useMemo(() => indexNodes(roots), [roots]);

    // DataGrid hardcodes the chevron's aria-label; this names each group's
    // toggle ("Collapse Assets" / "Expand Current assets") in the active
    // language. See `useChevronAriaLabels`.
    const gridRef = React.useRef<HTMLDivElement>(null);
    useChevronAriaLabels(gridRef, nodesById, expandedKeys, locale, t);

    const onRowAction = React.useCallback(
        (key: React.Key) => {
            const node = nodesById.get(String(key));
            // Gate navigation to postable leaves — header/aggregate rows have no
            // direct postings, so activating one must not route to the Journal.
            if (node && isNavigableAccountRow(node)) {
                onSelectAccount(node.account);
            }
        },
        [nodesById, onSelectAccount],
    );

    const columns = React.useMemo<DataGridColumn<AccountTreeNode>[]>(
        () => [
            {
                id: COA_COLUMN.account,
                header: t('chartOfAccounts.columns.account'),
                isRowHeader: true,
                // Virtualized mode lays the columns out with flexbox rather
                // than table auto-sizing, so the name column has to claim the
                // slack explicitly — without `1fr` it collapses to its minimum
                // and truncates ("Accumulated amortization — compu…").
                width: '1fr',
                minWidth: 260,
                cell: (node) => {
                    const name = node.account.name;
                    return (
                        <span
                            className={cn(
                                'flex min-w-0 grow items-center gap-2',
                                node.isArchived && 'opacity-55',
                            )}
                        >
                            <span
                                className={cn(
                                    'truncate text-sm',
                                    getAccountNameClassName(node),
                                )}
                                {...getNodeTestIdProp(
                                    `chart-of-accounts-name-${node.account.id}`,
                                )}
                            >
                                {name}
                            </span>
                            {/* Hover / focus-revealed row actions (see the
                                `.coa-data-grid [data-coa-row-actions]` CSS).
                                Real focusable buttons — the whole-row press still
                                navigates a leaf, but React-Aria doesn't fire the
                                row action when an inner button is pressed. */}
                            <span
                                className="ml-auto flex items-center gap-0.5 pl-3"
                                data-coa-row-actions=""
                            >
                                {isNavigableAccountRow(node) ? (
                                    // "Opens entries" hint — decorative; the
                                    // whole row is the navigation button.
                                    <span
                                        aria-hidden
                                        className="flex items-center gap-1 whitespace-nowrap pr-1 text-xs font-medium text-accent"
                                        data-coa-view-entries=""
                                        {...getNodeTestIdProp(
                                            `chart-of-accounts-view-entries-${node.account.id}`,
                                        )}
                                    >
                                        {t('chartOfAccounts.tree.viewEntries')}
                                        <Icon
                                            aria-hidden
                                            className="size-3.5"
                                            icon="gravity-ui:arrow-right"
                                        />
                                    </span>
                                ) : null}
                                {/* The accessible name is carried by a visually
                                    hidden span, not `aria-label`: HeroUI's
                                    Tooltip wrapper drops an `aria-label` set on
                                    its trigger Button (verified in the DOM), so
                                    an icon-only trigger ends up nameless. Text
                                    content always survives, and naming the row
                                    ("Edit Cash on hand") keeps the button
                                    distinguishable from the sibling chevron. */}
                                {canWrite ? (
                                    <Tooltip delay={0}>
                                        <Button
                                            isIconOnly
                                            onPress={() =>
                                                onEditAccount(node.account)
                                            }
                                            size="sm"
                                            variant="ghost"
                                            {...getNodeTestIdProp(
                                                `chart-of-accounts-edit-${node.account.id}`,
                                            )}
                                        >
                                            <Icon
                                                aria-hidden
                                                icon="gravity-ui:pencil"
                                            />
                                            <span className="sr-only">
                                                {t(
                                                    'chartOfAccounts.tree.editAccount',
                                                    { name },
                                                )}
                                            </span>
                                        </Button>
                                        <Tooltip.Content>
                                            <p>
                                                {t(
                                                    'chartOfAccounts.actions.edit',
                                                )}
                                            </p>
                                        </Tooltip.Content>
                                    </Tooltip>
                                ) : null}
                                {canWrite && node.isArchived ? (
                                    <Tooltip delay={0}>
                                        <Button
                                            isIconOnly
                                            onPress={() =>
                                                onReactivateAccount(
                                                    node.account,
                                                )
                                            }
                                            size="sm"
                                            variant="ghost"
                                            {...getNodeTestIdProp(
                                                `chart-of-accounts-reactivate-${node.account.id}`,
                                            )}
                                        >
                                            <Icon
                                                aria-hidden
                                                icon="gravity-ui:arrow-rotate-left"
                                            />
                                            <span className="sr-only">
                                                {t(
                                                    'chartOfAccounts.tree.reactivateAccount',
                                                    { name },
                                                )}
                                            </span>
                                        </Button>
                                        <Tooltip.Content>
                                            <p>
                                                {t(
                                                    'chartOfAccounts.actions.reactivate',
                                                )}
                                            </p>
                                        </Tooltip.Content>
                                    </Tooltip>
                                ) : null}
                            </span>
                        </span>
                    );
                },
            },
            {
                id: COA_COLUMN.code,
                header: t('chartOfAccounts.columns.code'),
                width: 110,
                minWidth: 96,
                cell: (node) => (
                    <span
                        className={cn(
                            'text-sm tabular-nums text-muted',
                            node.isArchived && 'opacity-55',
                        )}
                    >
                        {node.account.code}
                    </span>
                ),
            },
            {
                id: COA_COLUMN.type,
                header: t('chartOfAccounts.columns.type'),
                width: 132,
                minWidth: 120,
                // Shown on every row, headers included. A class or group carries
                // the same type as the accounts beneath it, and at four levels
                // deep the type is exactly what a collapsed branch should still
                // tell you.
                cell: (node) => (
                    <span className={cn(node.isArchived && 'opacity-55')}>
                        <AccountTypeChip
                            accountId={node.account.id}
                            type={node.account.type}
                        />
                    </span>
                ),
            },
            {
                id: COA_COLUMN.balance,
                header: t('chartOfAccounts.columns.balance'),
                align: 'end',
                width: 160,
                minWidth: 140,
                cell: (node) => (
                    <span
                        className={cn(
                            getBalanceClassName(node.displayTetri),
                            node.isArchived && 'opacity-55',
                        )}
                        {...getNodeTestIdProp(
                            `chart-of-accounts-balance-${node.account.id}`,
                        )}
                    >
                        {formatGel(node.displayTetri)}
                    </span>
                ),
            },
        ],
        [t, onEditAccount, onReactivateAccount, canWrite],
    );

    return (
        // `contents` wrapper: a ref handle for `useChevronAriaLabels` that adds
        // no box of its own, so DataGrid's layout and scoped CSS are untouched.
        <div ref={gridRef} className="contents">
            <DataGrid<AccountTreeNode>
                virtualized
                aria-label={t('chartOfAccounts.tree.ariaLabel')}
                className="coa-data-grid"
                columns={columns}
                contentClassName={COA_CONTENT_CLASS}
                data={roots}
                expandedKeys={expandedKeys}
                headingHeight={COA_HEADING_HEIGHT_PX}
                rowHeight={COA_ROW_HEIGHT_PX}
                getChildren={(node) =>
                    node.children.length > 0 ? node.children : undefined
                }
                getRowId={(node) => node.account.id}
                onExpandedChange={onExpandedChange}
                onRowAction={onRowAction}
                renderEmptyState={() => (
                    <div
                        className="flex flex-col items-center gap-3 py-12 text-muted"
                        {...getNodeTestIdProp('chart-of-accounts-empty')}
                    >
                        <Icon
                            aria-hidden
                            className="size-8 text-default-500"
                            icon="gravity-ui:folder-open"
                        />
                        <span className="text-sm">
                            {t('chartOfAccounts.empty')}
                        </span>
                    </div>
                )}
                treeColumn={COA_TREE_COLUMN}
                treeIndent={COA_INDENT_STEP_PX}
                variant="primary"
            />
        </div>
    );
};
